<template>
  <transition name="auth-modal-fade">
    <div
      v-if="showModal"
      class="cc-modal"
      @click.self="closePreferencesModal"
      role="dialog"
      aria-modal="true"
      :aria-label="$t('cookieConsent.preferencesTitle')"
      @keydown.esc="closePreferencesModal"
    >
      <div class="cc-modal__dialog">
        <!-- Header -->
        <div class="cc-modal__header">
          <div class="cc-modal__header-inner">
            <div class="cc-modal__header-icon" aria-hidden="true">
              <Cookie :size="22" :stroke-width="1.8" />
            </div>
            <p class="cc-modal__title">{{ $t('cookieConsent.preferencesTitle') }}</p>
            <button
              type="button"
              class="cc-modal__close"
              :aria-label="$t('common.close')"
              @click="closePreferencesModal"
            >
              <X :size="16" :stroke-width="2.5" />
            </button>
          </div>
        </div>

        <!-- Body -->
        <div class="cc-modal__body">
          <p class="cc-modal__intro">{{ $t('cookieConsent.preferencesIntro') }}</p>

          <!-- Necessary -->
          <div class="cc-category cc-category--locked">
            <div class="cc-category__info">
              <span class="cc-category__name">{{ $t('cookieConsent.necessary') }}</span>
              <span class="cc-category__desc">{{ $t('cookieConsent.necessaryDesc') }}</span>
            </div>
            <span class="cc-always-active">{{ $t('cookieConsent.alwaysActive') }}</span>
          </div>

          <!-- Analytics -->
          <div class="cc-category">
            <div class="cc-category__info">
              <span class="cc-category__name">{{ $t('cookieConsent.analytics') }}</span>
              <span class="cc-category__desc">{{ $t('cookieConsent.analyticsDesc') }}</span>
            </div>
            <label class="cc-toggle" :aria-label="$t('cookieConsent.analytics')">
              <input type="checkbox" v-model="localPrefs.analytics" />
              <span class="cc-toggle__track"></span>
            </label>
          </div>

          <!-- Marketing -->
          <div class="cc-category">
            <div class="cc-category__info">
              <span class="cc-category__name">{{ $t('cookieConsent.marketing') }}</span>
              <span class="cc-category__desc">{{ $t('cookieConsent.marketingDesc') }}</span>
            </div>
            <label class="cc-toggle" :aria-label="$t('cookieConsent.marketing')">
              <input type="checkbox" v-model="localPrefs.marketing" />
              <span class="cc-toggle__track"></span>
            </label>
          </div>

          <!-- Functional -->
          <div class="cc-category">
            <div class="cc-category__info">
              <span class="cc-category__name">{{ $t('cookieConsent.functional') }}</span>
              <span class="cc-category__desc">{{ $t('cookieConsent.functionalDesc') }}</span>
            </div>
            <label class="cc-toggle" :aria-label="$t('cookieConsent.functional')">
              <input type="checkbox" v-model="localPrefs.functional" />
              <span class="cc-toggle__track"></span>
            </label>
          </div>
        </div>

        <!-- Footer -->
        <div class="cc-modal__footer">
          <div class="cc-modal__footer-inner" style="justify-content: flex-end;">
            <div class="cc-modal__footer-right">
              <button type="button" class="cc-btn cc-btn--outline" @click="handleAcceptAll">
                {{ $t('cookieConsent.acceptAll') }}
              </button>
              <button type="button" class="cc-btn cc-btn--primary" @click="handleSave">
                {{ $t('cookieConsent.save') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { defineComponent, reactive, watch } from 'vue'
import { useConsent } from '@/composables/useConsent'
import { Cookie, X } from '@lucide/vue'

export default defineComponent({
  name: 'CookiePreferencesModal',
  components: { Cookie, X },
  setup () {
    const { showModal, savedPrefs, acceptAll, rejectAll, savePreferences, closePreferencesModal } = useConsent()

    const localPrefs = reactive({
      analytics: false,
      marketing: false,
      functional: false
    })

    // Sync local state from saved prefs whenever modal opens
    watch(showModal, (open) => {
      if (!open) return
      const p = savedPrefs.value
      localPrefs.analytics = p ? Boolean(p.analytics) : false
      localPrefs.marketing = p ? Boolean(p.marketing) : false
      localPrefs.functional = p ? Boolean(p.functional) : false
    })

    function handleSave() {
      savePreferences({ ...localPrefs })
    }

    function handleAcceptAll() {
      localPrefs.analytics = true
      localPrefs.marketing = true
      localPrefs.functional = true
      acceptAll()
    }

    function handleRejectAll() {
      localPrefs.analytics = false
      localPrefs.marketing = false
      localPrefs.functional = false
      rejectAll()
    }

    return {
      showModal,
      localPrefs,
      handleSave,
      handleAcceptAll,
      handleRejectAll,
      closePreferencesModal
    }
  }
})
</script>

<style scoped lang="scss">
@import '@/styles/brand-tokens';

.cc-modal {
  position: fixed;
  inset: 0;
  z-index: 9995;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  padding-top: calc(16px + var(--app-safe-top));
  padding-bottom: calc(16px + var(--app-safe-bottom));
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(2px);
}

.cc-modal__dialog {
  width: min(100%, 560px);
  max-height: min(700px, calc(100dvh - 32px - var(--app-safe-top) - var(--app-safe-bottom)));
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #ffffff;
  border-radius: 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.18);
}

// ─── Header ──────────────────────────────────────────────────────────────────

.cc-modal__header {
  flex-shrink: 0;
  border-bottom: 1px solid #eef2f7;
}

.cc-modal__header-inner {
  min-height: 58px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 20px;
}

.cc-modal__header-icon {
  flex: 0 0 auto;
  color: $brand-color;

  svg {
    width: 22px;
    height: 22px;
  }
}

.cc-modal__title {
  flex: 1 1 auto;
  min-width: 0;
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: #111827;
}

.cc-modal__close {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: none;
  border-radius: 50%;
  background: rgba(17, 24, 39, 0.72);
  backdrop-filter: blur(8px);
  color: #ffffff;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.6);
  }
}

// ─── Body ─────────────────────────────────────────────────────────────────────

.cc-modal__body {
  flex: 1 1 auto;
  min-height: 0;
  padding: 20px;
  overflow-y: auto;
  overflow-x: hidden;

  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 999px; }
}

.cc-modal__intro {
  margin: 0 0 18px;
  font-size: 13px;
  color: #6b7280;
  line-height: 1.6;
}

// ─── Category rows ────────────────────────────────────────────────────────────

.cc-category {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 0;
  border-bottom: 1px solid #f3f4f6;

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }

  &--locked {
    opacity: 0.8;
  }
}

.cc-category__info {
  display: flex;
  flex-direction: column;
  gap: 3px;
  flex: 1 1 auto;
  min-width: 0;
}

.cc-category__name {
  font-size: 13px;
  font-weight: 700;
  color: #111827;
}

.cc-category__desc {
  font-size: 12px;
  color: #6b7280;
  line-height: 1.5;
}

.cc-category__tools {
  font-size: 11px;
  color: #9ca3af;
  font-style: italic;
  margin-top: 2px;
}

.cc-always-active {
  flex: 0 0 auto;
  font-size: 11px;
  font-weight: 700;
  color: #16a34a;
  background: #dcfce7;
  padding: 3px 10px;
  border-radius: 999px;
  white-space: nowrap;
  align-self: center;
}

// ─── Toggle switch ────────────────────────────────────────────────────────────

.cc-toggle {
  flex: 0 0 auto;
  position: relative;
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  align-self: center;

  input {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
  }
}

.cc-toggle__track {
  display: block;
  width: 44px;
  height: 24px;
  border-radius: 999px;
  background: #d1d5db;
  transition: background 0.2s ease;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 3px;
    left: 3px;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #ffffff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
    transition: transform 0.2s ease;
  }
}

.cc-toggle input:checked ~ .cc-toggle__track {
  background: $brand-color;

  &::after {
    transform: translateX(20px);
  }
}

.cc-toggle input:focus-visible ~ .cc-toggle__track {
  outline: 2px solid $brand-color;
  outline-offset: 2px;
}

// ─── Footer ───────────────────────────────────────────────────────────────────

.cc-modal__footer {
  flex-shrink: 0;
  border-top: 1px solid #e5e7eb;
  background: #ffffff;
}

.cc-modal__footer-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 16px 20px;
  flex-wrap: wrap;
}

.cc-modal__footer-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

// ─── Buttons ──────────────────────────────────────────────────────────────────

.cc-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 38px;
  padding: 0 18px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;

  &--primary {
    background: $brand-color;
    border: 1.5px solid transparent;
    color: #ffffff;

    &:hover {
      background: color-mix(in srgb, $brand-color 88%, #111827);
    }
  }

  &--outline {
    background: transparent;
    border: 1.5px solid #d1d5db;
    color: #374151;

    &:hover {
      border-color: $brand-color;
      color: $brand-color;
    }
  }

  &--ghost {
    background: transparent;
    border: 1.5px solid transparent;
    color: #6b7280;
    padding: 0 12px;

    &:hover {
      color: #374151;
    }
  }
}

// ─── Mobile ───────────────────────────────────────────────────────────────────

@media (max-width: 575px) {
  .cc-modal {
    padding: 16px;
    padding-top: calc(16px + var(--app-safe-top));
    padding-bottom: calc(16px + var(--app-safe-bottom));
    align-items: center;
    justify-content: center;
  }

  .cc-modal__dialog {
    width: calc(100% - 32px);
    max-height: calc(100dvh - 32px - var(--app-safe-top) - var(--app-safe-bottom));
    border-radius: 20px;
  }

  .cc-modal__footer-inner {
    flex-direction: column;
    align-items: stretch;
  }

  .cc-modal__footer-right {
    flex-direction: column-reverse;

    .cc-btn {
      width: 100%;
      justify-content: center;
    }
  }

  .cc-btn--ghost {
    width: 100%;
    justify-content: center;
  }
}
</style>
